import { format } from 'date-fns';
import * as React from 'react';
import { FiCalendar } from 'react-icons/fi';
import { Day, useLilius } from 'use-lilius';
import { merge } from '../../../helpers/Utility';
import { InputProps, withField } from '../../form/withField';
import { Button } from '../button/Button';
import { Icon } from '../icons/Icon';
import { Modal } from '../modal/Modal';
import { DatePicker, DayLabels } from './DatePicker';

const defaultLabels = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tues',
  Wednesday: 'Wed',
  Thursday: 'Thurs',
  Friday: 'Fri',
  Saturday: 'Sat'
};

interface DateInputProps extends InputProps<Date | undefined> {
  name?: string;
  labels?: DayLabels;
  minDate?: Date;
  displayFormat?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className,
  name,
  labels = defaultLabels,
  minDate,
  displayFormat
}) => {
  const { calendar, inRange, isSelected, select, selected, setViewing, viewing, viewNextMonth, viewPreviousMonth } =
    useLilius({ weekStartsOn: Day.MONDAY });

  React.useEffect(() => {
    if (!value) {
      setViewing(new Date());
      return;
    }
    setViewing(value);
    select(value, true);
  }, [value, select, setViewing]);

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const handleDayClick = (day: Date) => {
    if (minDate && day < minDate) return;
    onChange?.(day);
    setIsOpen(false);
  };

  const isPreviousMonthDisabled = minDate ? format(viewing, 'yyyy-MM') <= format(minDate, 'yyyy-MM') : false;

  return (
    <div className={merge(className, 'w-full flex relative')}>
      <Button onClick={() => setIsOpen(true)} className={'w-full flex items-center space-x-3'}>
        <Icon small Icon={FiCalendar} />
        {selected[0] ? (
          <span className="font-medium">
            {displayFormat ? (
              format(selected[0], displayFormat)
            ) : (
              <>
                <span className="sm:block hidden">{format(selected[0], 'EEEE do MMMM yyyy')}</span>
                <span className="sm:hidden block">{format(selected[0], 'E do MMM')}</span>
              </>
            )}
          </span>
        ) : (
          <span className="text-secondary-std">{'No selected'}</span>
        )}
      </Button>

      <Modal open={isOpen} onClose={() => setIsOpen(false)} className="pb-10">
        <DatePicker
          dismiss={() => setIsOpen(false)}
          calendar={calendar[0]}
          selected={selected}
          viewing={viewing}
          inRange={inRange}
          isSelected={isSelected}
          viewPreviousMonth={viewPreviousMonth}
          viewNextMonth={viewNextMonth}
          onDayClick={handleDayClick}
          labels={labels}
          minDate={minDate}
          isPreviousMonthDisabled={isPreviousMonthDisabled}
          hideDismiss
        />
      </Modal>
    </div>
  );
};

export const DateField = withField(DateInput);
